import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { logWaiterConversion } from '@/utils/ticketEventLogger';
import { mutateTicketSchedule } from '@/utils/mutateTicketSchedule';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { useAuth } from '@/contexts/AuthContext';
import { clock } from '@/utils/clock';
import { usePermissions } from '@/hooks/usePermissions';
// Core-100 R4 CORE-BE: tomorrow's capacity is shown before the carry; the front office states why it carries.
import { useCarryCapacityPreview } from '@/hooks/useCarryCapacityPreview';
import { describeCarryCapacity } from '@/lib/rollover/carryCapacity';
import { CARRY_REASONS, CARRY_REASON_LABEL, type CarryReason } from '@/lib/tickets/carryReason';
// Phase 10.3: rollover-promise planner (Phase 8.1) preflight before
// invoking the edge. Refuses rollover that would silently violate
// Due Out or Waiter promise.
import {
  getRolloverPromiseResolutionPlan,
  type RolloverPromiseTicketShape,
} from '@/utils/rolloverPromiseResolution';
// Phase 10.4: run-long EOD continuation planner (Phase 8.2). Validates
// the proposed Part B continuation BEFORE invoking the edge:
//   - remaining-time source (tech-entered > system estimate > etc.)
//   - Part A actuals preservation
//   - Phase 8.1 promise risks (Due Out / Waiter / physical) piped through
import {
  getRunLongEODContinuationPlan,
  type RunLongEODContinuationInput,
} from '@/utils/runLongEODContinuation';
// FLOYD 2026-10-26 (DEFECT 2): the carry window — the work's own day end, the remainder, the next valid window —
// from the SAME shared planner create-eod-rollover runs, never parsed out of the opener's `techEOD` text.
import {
  formatShopDayTime,
  loadStartedWorkCarryContext,
  planCarryFromContext,
  resolveAuthorizedBoundary,
  type StartedWorkCarryContextResult,
} from '@/lib/rollover/startedWorkCarryWindow';
// Static, not `await import(...)`: a chunk that a later deploy removed must not stand between the person and the
// carry (the 2026-10-26 deleteTicketFamily failure mode).
import { liveRolloverSuccessorId } from '@/utils/rolloverChain';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';

interface EODRolloverModalProps {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  customerName: string;
  techName: string;
  /**
   * DISPLAY ONLY — the opener's own wording of the end of day (often the weekly flat `shift_end`). The carry is
   * never planned from it: the modal reads the work's real window (see `startedWorkCarryWindow`).
   */
  techEOD: string;
  /**
   * The server's authorized end-of-day instant for the work's day (`rollover_conversions.detection.boundary_at`,
   * which includes AUTHORIZED overtime), when the opener has one. ISO-8601; null/absent = use the dated shift end.
   */
  authorizedBoundaryAt?: string | null;
  rolloverMinutes: number;
  /** When 'Waiter', the modal hides "Convert to Rollover" — rolling a
   *  Waiter breaks the customer's promised pickup. The server-side
   *  createEODRolloverSplit also refuses Waiters as defense-in-depth. */
  ticketType?: string | null;
  /**
   * The ticket already has a rollover continuation: its future work is
   * already carried, so "Convert to Rollover" is not offered (a second carry
   * would fork the chain; the server refuses it with ROLLOVER_SUCCESSOR_EXISTS).
   */
  alreadyCarried?: boolean;
  onReassign: () => void;
  onResumeAnyway?: () => Promise<void>;
}

export function EODRolloverModal({
  open,
  onClose,
  ticketId,
  customerName,
  techName,
  techEOD,
  authorizedBoundaryAt = null,
  rolloverMinutes,
  ticketType,
  alreadyCarried = false,
  onReassign,
  onResumeAnyway,
}: EODRolloverModalProps) {
  const isWaiter = ticketType === 'Waiter';
  // Convert is offered only to work that is not a Waiter and not already carried.
  const canConvert = !isWaiter && !alreadyCarried;
  const { activeShop } = useAuth();
  const [converting, setConverting] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [convertingToDropoff, setConvertingToDropoff] = useState(false);
  // Phase 10.4: technician-entered remaining minutes for Part B.
  // Optional — leaving blank uses the system-estimate fallback
  // (`rolloverMinutes`) via the Phase 8.2 planner's source priority.
  const [remainingMinutesInput, setRemainingMinutesInput] = useState('');
  // Core-100 R4 CORE-BE: why it carries — stated by the front office at the decision (optional; when left
  // unstated the server derives it from the ticket's state or records "not recorded", never a guess).
  const { canEditTickets: canStateCarryReason } = usePermissions();
  const [carryReason, setCarryReason] = useState<CarryReason | ''>('');
  const [carryReasonNote, setCarryReasonNote] = useState('');
  const enteredRemaining = Number.parseFloat(remainingMinutesInput);
  const carryMinutesForPreview = Number.isFinite(enteredRemaining) && enteredRemaining > 0 ? enteredRemaining : rolloverMinutes;
  const capacity = useCarryCapacityPreview(ticketId, carryMinutesForPreview, open && canConvert);

  // The work's real end of day, in shop time, for the sentence the person reads (the carry itself re-reads it).
  const [dayEnd, setDayEnd] = useState<{ label: string; stale: boolean; techName: string; estimateMinutes: number | null } | null>(null);
  useEffect(() => {
    if (!open || !ticketId) { setDayEnd(null); return; }
    let live = true;
    void loadStartedWorkCarryContext(ticketId).then((r) => {
      if (!live || r.state !== 'ready') return;
      const carry = planCarryFromContext(r.context, null);
      if (carry.state === 'no_shift_window') return;
      const end = resolveAuthorizedBoundary({ normalEndAt: carry.shiftEndAt, authorizedBoundaryAt,
        scheduledDate: r.context.scheduledDate, shopTimezone: r.context.shopTimezone });
      setDayEnd({ label: formatShopDayTime(end.at, r.context.shopTimezone), stale: r.context.stale, techName: r.context.techName,
        estimateMinutes: carry.systemEstimatedRemainingMinutes > 0 ? Math.round(carry.systemEstimatedRemainingMinutes) : null });
    }, () => { /* the sentence falls back to the opener's wording; the carry re-reads and refuses on its own */ });
    return () => { live = false; };
  }, [open, ticketId, authorizedBoundaryAt]);
  const shownTechName = techName || dayEnd?.techName || 'the technician';
  // The opener's wording is shown only when it IS a time of day — never a raw instant, never an empty "()".
  const shownEod = dayEnd?.label ?? (/^\d{1,2}:\d{2}/.test(techEOD ?? '') ? techEOD : null);

  // Reset disabled/loading states whenever the modal opens
  useEffect(() => {
    if (open) {
      setConverting(false);
      setReassigning(false);
      setResuming(false);
      setConvertingToDropoff(false);
      setRemainingMinutesInput('');
      setCarryReason('');
      setCarryReasonNote('');
    }
  }, [open]);

  // Chronomancer R-WAITER-4: canonical Waiter → Drop-off conversion.
  // EODRolloverModal tells Waiters to "convert this ticket to a Drop-off
  // first to release the promise" but previously exposed no button to do
  // it, forcing the advisor to manually dismiss the modal and open
  // EditTicketDetailsModal from the ticket menu — high-friction for a
  // customer sitting in the lobby. The orphan `WaiterConversionPrompt.tsx`
  // component promised this conversion via an `onConvert` callback nobody
  // wired up. This handler executes the canonical conversion in-place:
  //   - type: 'Waiter' → 'Drop-off'
  //   - scheduled_start_time: null (release the appointment lock)
  //   - due_out: kept (customer still wants car back at promised time;
  //     the promise is just no longer bound to physical waiting)
  //   - start_time: kept (cascade will reflow now that the lock is gone)
  // logWaiterConversion preserves the audit trail + advisor metrics.
  const handleConvertToDropoff = async () => {
    setConvertingToDropoff(true);
    try {
      const { data: ticketRow, error: fetchError } = await supabase
        .from('tickets')
        .select('shop_id, scheduled_date, assigned_tech_id')
        .eq('id', ticketId)
        .maybeSingle();
      if (fetchError) throw fetchError;

      // V-A-8: routed through mutateTicketSchedule. scheduled_start_time
      // is the schedule field; type is companion metadata that must land
      // atomically with the schedule write. skipCascade:true preserves
      // the bespoke triggerBoardCascade below (scoped by tech + scheduled_date).
      const mutateResult = await mutateTicketSchedule(
        ticketId,
        { scheduled_start_time: null },
        {
          reason: 'eod_rollover_convert_to_dropoff',
          skipCascade: true,
          companionFields: { type: 'Drop-off' },
        }
      );
      if (!mutateResult.success) throw new Error(mutateResult.error || 'Conversion update failed');

      // Chronomancer R-WAITER-8: trigger cascade after conversion. The
      // Waiter lock is now released; downstream tickets on this tech
      // should reflow to take advantage of the new flexibility. Without
      // this, the converted Drop-off sits at its old anchored start_time
      // until some other change nudges cascade (R-WAITER-7 now monitors
      // type + scheduled_start_time, but the realtime hook runs on the
      // *viewer's* page — if nobody has Dayboard open, no cascade fires).
      if (ticketRow?.shop_id && ticketRow?.scheduled_date) {
        try {
          await triggerBoardCascade(
            ticketRow.shop_id,
            ticketRow.scheduled_date,
            ticketRow.assigned_tech_id ?? undefined,
          );
        } catch (cascadeErr) {
          console.warn('[EODRolloverModal] Cascade after conversion failed (non-fatal):', cascadeErr);
        }
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        const { data: shopRow } = await supabase
          .from('tickets')
          .select('shop_id')
          .eq('id', ticketId)
          .maybeSingle();
        if (user?.id && shopRow?.shop_id) {
          await logWaiterConversion({
            ticketId,
            shopId: shopRow.shop_id,
            userId: user.id,
            reason: 'eod_exceeded_manual_conversion',
          });
        }
      } catch (logErr) {
        console.error('[EODRolloverModal] logWaiterConversion error:', logErr);
      }

      toast({
        title: 'Converted to Drop-off',
        description: `${customerName}'s ticket is now a Drop-off. The appointment lock has been released.`,
      });
      onClose();
      void notifyDayboardInvalidated({
        reason: 'eod-rollover-save',
        shopId: activeShop?.id || '',
        ticketId,
        metadata: { kind: 'waiter-to-dropoff' },
      });
    } catch (error) {
      console.error('[EODRolloverModal] Waiter → Drop-off conversion failed:', error);
      toast({
        title: 'Conversion Failed',
        description: 'Could not convert ticket to Drop-off. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setConvertingToDropoff(false);
    }
  };

  const handleResumeAnyway = async () => {
    if (!onResumeAnyway) return;
    setResuming(true);
    try {
      await onResumeAnyway();
      onClose();
    } catch (error) {
      console.error('Error resuming timer:', error);
    } finally {
      setResuming(false);
    }
  };

  const handleConvertToRollover = async () => {
    setConverting(true);
    try {
      // A ticket that already continues has its future carried: never a
      // second carry. Re-checked first, before anything else — the modal may
      // have been opened on a state that has since changed (the server
      // refuses a fork regardless: ROLLOVER_SUCCESSOR_EXISTS).
      if (await liveRolloverSuccessorId(ticketId)) {
        toast({
          title: 'Already carried',
          description: 'This ticket already has a rollover continuation — its remaining work is already scheduled.',
        });
        onClose();
        return;
      }

// Phase 10.3: Rollover-promise planner preflight (Phase 8.1).
      // Fetches the parent ticket's promise-relevant fields and runs the
      // canonical Phase 8.1 verdict. If the planner returns a non-`safe`
      // state, surface the doctrinal blocker via toast and refuse to
      // invoke the edge — the existing collect-and-invoke path is unsafe
      // for promises (Waiter / Due Out) that the planner pins.
      //
      // Projected Part B finish is a CONSERVATIVE proxy:
      //   `now + rolloverMinutes * 60_000`
      // Tomorrow's next-day shift_start + remainingMinutes is necessarily
      // LATER than this anchor. So if this proxy already exceeds Due Out,
      // tomorrow's continuation certainly does — the planner block fires.
      // A future Phase 10.x enhancement can fetch tech.shift_start and
      // compute the exact next-day finish to catch additional Due Out
      // tomorrow violations.
      const { data: parentTicket, error: parentFetchErr } = await supabase
        .from('tickets')
        // Core-100 R4 CORE-BE: the run-long planner below checks Part A's actuals (actual start / minutes /
        // timer). They were never selected, so validatePartAActualsPreserved always found "timer_history_missing"
        // and every Convert to Rollover from this modal was refused before reaching the server.
        .select(
          'id, due_out, scheduled_start_time, type, status, picked_up_at, archived_at, deleted_at, actual_start_time, actually_started, actual_minutes, timer_status',
        )
        .eq('id', ticketId)
        .maybeSingle();
      if (parentFetchErr) throw parentFetchErr;
      if (!parentTicket) {
        throw new Error('Parent ticket not found for rollover preflight');
      }

      // FLOYD 2026-10-26 (DEFECT 2): the carry the SERVER would build — Part A's boundary on the work's own
      // shop-local day, the remainder (the technician's word, else OptiTime minus what that day held), and the
      // continuation days from the shared planner. Read fresh at the moment of the decision.
      const techEnteredRaw = Number.parseFloat(remainingMinutesInput);
      const techEnteredValid =
        Number.isFinite(techEnteredRaw) && techEnteredRaw > 0;
      const techEnteredMin: number | null = techEnteredValid
        ? techEnteredRaw
        : null;
      const nowDate = clock.now();
      const carryContext: StartedWorkCarryContextResult = await loadStartedWorkCarryContext(ticketId);
      const ctx = carryContext.state === 'ready' ? carryContext.context : null;
      const carry = ctx ? planCarryFromContext(ctx, techEnteredMin) : null;
      const workDayEnd = ctx && carry && carry.state !== 'no_shift_window'
        ? resolveAuthorizedBoundary({ normalEndAt: carry.shiftEndAt, authorizedBoundaryAt,
            scheduledDate: ctx.scheduledDate, shopTimezone: ctx.shopTimezone })
        : null;
      const systemEstimatedRemainingMin = carry && carry.state !== 'no_shift_window'
        && carry.systemEstimatedRemainingMinutes > 0 ? carry.systemEstimatedRemainingMinutes : null;

      // The server's remainder is the technician's word, else OptiTime minus what the work's day held — nothing
      // else (createEODRolloverSplit refuses a non-positive one). When that estimate says the work should already
      // be done, only a person can say what remains: ask, rather than let a weaker fallback in the preflight
      // approve a carry the server will refuse.
      if (carry?.state === 'no_remaining_time' && techEnteredMin == null) {
        toast({
          title: 'Remaining minutes needed',
          description: 'The schedule estimate says this work should already be finished, so it cannot say how much remains. Enter the remaining minutes, then convert.',
          variant: 'destructive',
        });
        return;
      }

      // Where the whole carry finishes is what a promise is measured against — the server measures the final
      // continuation's finish. Only when the carry cannot be planned (the run-long planner then refuses anyway)
      // does the conservative proxy remain: now + the remainder.
      const projectedPartBFinishAt = carry?.state === 'ready'
        ? carry.finalFinishAt
        : new Date(nowDate.getTime() + Math.max(0, techEnteredMin ?? systemEstimatedRemainingMin ?? rolloverMinutes) * 60_000);
      const plan = getRolloverPromiseResolutionPlan({
        parent: parentTicket as unknown as RolloverPromiseTicketShape,
        projectedPartBFinishAt,
        // actorRole intentionally null for Phase 10.3 — modal users do
        // not surface an "I am authorized to edit Due Out" toggle. The
        // planner will emit `due_out_edit_authorization_required` for
        // any Due Out violation. A future Phase 10.x can read the actor
        // role from `useAuth` + user_roles and pass it explicitly.
        actorRole: null,
      });

      if (plan.state !== 'safe') {
        console.warn(
          `[EODRolloverModal] Phase 10.3 rollover-promise planner blocked: ` +
            `state=${plan.state}; blockers=${plan.blockers.join(',') || '<none>'}; ` +
            `actions=${plan.actions.join(',') || '<none>'}`,
        );
        toast({
          title: 'Rollover Blocked',
          description:
            plan.state === 'due_out_violation' ||
            plan.state === 'authorized_due_out_change_required'
              ? `Tomorrow's continuation would miss the customer's promised pickup (Due Out). An authorized Due Out edit or breach record is required first.`
              : plan.state === 'waiter_promise_violation'
                ? `Rolling this work would break the Waiter promise. Reassign or convert to Drop-off first.`
                : plan.state === 'blocked_physical_constraint'
                  ? `Rollover blocked by a physical constraint (bay lockout, vehicle reality, or active rollover descendant).`
                  : `Rollover blocked: ${plan.state}. ${plan.blockers.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }

      // Phase 10.4: Run-long EOD continuation planner (Phase 8.2).
      // Layers on top of the Phase 10.3 promise planner with:
      //   - explicit remaining-time source selection (tech-entered >
      //     system estimate > prediction-minus-actual > default min)
      //   - Part A actuals preservation
      //   - explicit "stop and rollover" intent so the after-hours
      //     state ladder short-circuits to the continuation path
      //
      // The user is clicking "Convert to Rollover" — `intent` is
      // unambiguous `stop_and_rollover`.
      //
      // FLOYD 2026-10-26 (DEFECT 2): `shiftEnd` used to be parsed as "HH:mm"
      // out of `techEOD` and applied to TODAY with browser-local setHours;
      // the next window was now + 16 h. The decision panel passed
      // `String(detection.boundary_at ?? '')` — '' or an ISO instant — so
      // the parse failed, shiftEnd was undefined, and every approval of
      // started work was refused for `missing_shift_window`. The planner was
      // right to refuse; it had been handed no window.
      //
      // Now every time input is the authority's own: the end of the work's
      // OWN shop-local day (the server's authorized boundary when it has one
      // — authorized overtime included — else the dated normal-hours end),
      // the remainder the server would compute, and the first valid
      // continuation window from the shared planner. Nothing is substituted
      // when a fact is unknown: the planner still refuses missing context.
      const runLongInput: RunLongEODContinuationInput = {
        parent: parentTicket as unknown as RolloverPromiseTicketShape,
        now: nowDate,
        intent: 'stop_and_rollover',
        shiftEnd: workDayEnd?.at,
        technicianEnteredRemainingMin: techEnteredMin,
        systemEstimatedRemainingMin,
        projectedPartBFinishAt,
        nextValidWindowStartAt: carry?.state === 'ready' ? carry.nextWindowStartAt : null,
        noValidNextWindow: carry?.state === 'no_valid_next_window',
        actorRole: null,
      };
      const runLongPlan = getRunLongEODContinuationPlan(runLongInput);
      const SAFE_RUN_LONG_VERDICTS = new Set<string>([
        'create_part_b_continuation',
        'uses_system_estimate_fallback',
        'continue_past_eod_safe',
      ]);
      if (!SAFE_RUN_LONG_VERDICTS.has(runLongPlan.verdict)) {
        console.warn(
          `[EODRolloverModal] Phase 10.4 run-long EOD continuation planner ` +
            `blocked: verdict=${runLongPlan.verdict}; blockers=` +
            `${runLongPlan.blockers.join(',') || '<none>'}; actions=` +
            `${runLongPlan.actions.join(',') || '<none>'}`,
        );
        const whose = shownTechName === 'the technician' ? "the technician's" : `${shownTechName}'s`;
        const desc =
          runLongPlan.blockers.includes('missing_shift_window')
            ? carryContext.state === 'missing'
              ? carryContext.reason === 'ticket_unscheduled'
                ? `This ticket has no technician, day or start time recorded, so the end of its working day cannot be established. Nothing was carried.`
                : carryContext.reason === 'shop_timezone_unknown'
                ? `The shop's time zone is not set (or could not be read), so the end of the work's day cannot be established. Nothing was carried — set the time zone in Shop Profile, then decide again.`
                : `The ticket or ${whose} schedule could not be read, so the end of the work's day cannot be established. Nothing was carried — try again.`
              : `The end of ${whose} working day${ctx ? ` on ${ctx.scheduledDate}` : ''} is not known: no hours are set for that day and no weekly shift is recorded. Set the hours, then decide again. Nothing was carried.`
            : runLongPlan.verdict === 'requires_remaining_time_prompt'
            ? `The continuation needs an explicit remaining-minutes value before we can create Part B. Enter a value above and try again.`
            : runLongPlan.verdict === 'requires_authorized_due_out_change' ||
                runLongPlan.verdict === 'blocked_due_out_promise' ||
                runLongPlan.verdict === 'requires_breach_record'
              ? `Tomorrow's continuation would breach Due Out. An authorized Due Out edit or breach record is required first.`
              : runLongPlan.verdict === 'blocked_waiter_promise'
                ? `Continuing would break the Waiter promise. Reassign or convert to Drop-off first.`
                : runLongPlan.verdict === 'blocked_locked_bay'
                  ? `The proposed next-day bay is locked out. Resolve the lockout or choose a different bay.`
                  : runLongPlan.verdict === 'blocked_physical_reality'
                    ? `Continuation blocked by a vehicle physical reality conflict (same vehicle in two places, or an active rollover descendant).`
                    : runLongPlan.verdict === 'blocked_no_valid_next_window'
                      ? `No valid next working day found within the lookahead window.`
                      : `Continuation blocked (${runLongPlan.verdict}): ${runLongPlan.blockers.join(', ') || 'manual review required'}.`;
        toast({
          title: 'Rollover Blocked',
          description: desc,
          variant: 'destructive',
        });
        return;
      }

      // Route through canonical server-side rollover creation.
      // Phase 10.4: pass the tech-entered remaining minutes when
      // present; the edge function forwards as `remainingMinutesOverride`
      // to `createEODRolloverSplit`. Absent → server uses the existing
      // `opti_time - todayMinutes` system estimate.
      const { data, error } = await supabase.functions.invoke('create-eod-rollover', {
        body: {
          ...(techEnteredValid ? { ticketId, remainingMinutes: techEnteredMin } : { ticketId }),
          // Core-100 R4 CORE-BE: the stated reason travels with the decision (front office only).
          ...(canStateCarryReason && carryReason
            ? { carryReason, carryReasonNote: carryReasonNote.trim() || null }
            : {}),
        },
      });

      if (error) throw new Error(error.message || 'Rollover creation failed');
      if (!data?.success) throw new Error(data?.error || 'Rollover creation failed');
      
      toast({
        title: 'Converted to Rollover',
        description: `Ticket for ${customerName} has been converted to a rollover ticket.`,
      });

      onClose();
      void notifyDayboardInvalidated({
        reason: 'eod-rollover-save',
        shopId: activeShop?.id || '',
        ticketId,
      });
    } catch (error) {
      console.error('Error converting to rollover:', error);
      toast({
        title: 'Conversion Failed',
        description: 'Could not convert ticket to rollover. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setConverting(false);
    }
  };

  const handleReassign = () => {
    setReassigning(true);
    onReassign();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent data-testid="eod-rollover-modal" className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <DialogTitle>End of Day Exceeded</DialogTitle>
          </div>
          <DialogDescription className="space-y-2 pt-2">
            {dayEnd?.stale ? (
              // A decision made on a later day: the work's own day is over (FD-11). Saying "will extend past … by
              // 0 minutes" about a day that ended days ago is how today's open hours and "day already ended" came
              // to look like a contradiction on 2026-10-26.
              <p data-testid="eod-rollover-day-ended">
                The work on the ticket for <strong>{customerName}</strong> stopped unfinished when{' '}
                <strong>{shownTechName}'s</strong> working day ended ({dayEnd.label}). Carrying it continues the
                remaining work from today or the next working day.
              </p>
            ) : (
              <p>
                The ticket for <strong>{customerName}</strong> will extend past{' '}
                <strong>{shownTechName}'s</strong> end of day{shownEod ? ` (${shownEod})` : ''}
                {rolloverMinutes > 0 ? <> by <strong>{rolloverMinutes} minutes</strong></> : null}.
              </p>
            )}
            {isWaiter ? (
              <p className="text-sm text-destructive font-medium">
                ⚠️ This is a Waiter appointment — the customer is waiting in
                the lobby. Rolling this work to tomorrow would break the
                promised pickup. Reassign to another technician who can
                finish in time, or (if the customer agrees) convert this
                ticket to a Drop-off first to release the promise.
              </p>
            ) : alreadyCarried ? (
              <p className="text-sm text-muted-foreground" data-testid="eod-rollover-already-carried">
                This work is already carried to a later day — the ticket has a rollover continuation, so
                it cannot be converted again. Its remaining work is scheduled on that continuation; you can
                reassign the ticket instead.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                You can either reassign this ticket to another available technician or convert it to a rollover ticket for tomorrow.
              </p>
            )}
          </DialogDescription>
        </DialogHeader>

        {canConvert && (
          <div className="space-y-1.5 pt-1">
            <Label
              htmlFor="eod-rollover-remaining-minutes"
              className="text-xs text-muted-foreground"
            >
              {dayEnd && dayEnd.estimateMinutes == null ? 'Remaining minutes (required)' : 'Remaining minutes (optional)'}
            </Label>
            <Input
              id="eod-rollover-remaining-minutes"
              data-testid="eod-rollover-remaining-minutes"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              placeholder={dayEnd && dayEnd.estimateMinutes == null
                ? 'The estimate says this work is done — enter what remains'
                : `Leave blank to use the system estimate (${dayEnd?.estimateMinutes ?? rolloverMinutes} min)`}
              value={remainingMinutesInput}
              onChange={e => setRemainingMinutesInput(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Tech-entered value outranks the system estimate when creating Part B.
            </p>
            <p className="text-xs pt-1" data-testid="eod-carry-capacity" role="status">
              {capacity.isLoading && 'Checking the next working day…'}
              {capacity.isError && 'The next working day could not be checked — this is not a sign that it has room.'}
              {capacity.isSuccess && (capacity.data
                ? <span className={capacity.data.preview.state === 'known' && capacity.data.preview.overByMinutes > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}>
                    Next working day — {describeCarryCapacity(capacity.data.preview, capacity.data.techName)}
                  </span>
                : 'No technician or day is set on this ticket, so the next working day cannot be checked.')}
            </p>
            {canStateCarryReason && (
              <div className="space-y-1.5 pt-1">
                <Label htmlFor="eod-carry-reason" className="text-xs text-muted-foreground">Why it carries (optional)</Label>
                <select
                  id="eod-carry-reason"
                  data-testid="eod-carry-reason"
                  className="h-10 w-full rounded-md border bg-background px-2 text-sm"
                  value={carryReason}
                  onChange={(e) => setCarryReason(e.target.value as CarryReason | '')}
                >
                  <option value="">Not stated — read from the ticket's state</option>
                  {CARRY_REASONS.map((r) => <option key={r} value={r}>{CARRY_REASON_LABEL[r]}</option>)}
                </select>
                {carryReason && (
                  <Input
                    data-testid="eod-carry-reason-note"
                    placeholder="Note (optional)"
                    maxLength={500}
                    value={carryReasonNote}
                    onChange={(e) => setCarryReasonNote(e.target.value)}
                  />
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row sm:flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Dismiss
          </Button>
          {onResumeAnyway && (
            <Button
              variant="secondary"
              onClick={handleResumeAnyway}
              disabled={resuming}
              className="w-full sm:w-auto"
            >
              {resuming ? 'Resuming...' : 'Resume Anyway'}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={handleReassign}
            disabled={reassigning}
            className="w-full sm:w-auto"
          >
            Reassign Ticket
          </Button>
          {isWaiter && (
            <Button
              data-testid="convert-to-dropoff-button"
              variant="default"
              onClick={handleConvertToDropoff}
              disabled={convertingToDropoff}
              className="w-full sm:w-auto"
            >
              {convertingToDropoff ? 'Converting...' : 'Convert to Drop-off'}
            </Button>
          )}
          {canConvert && (
            <Button
              data-testid="confirm-rollover-button"
              variant="destructive"
              onClick={handleConvertToRollover}
              disabled={converting}
              className="w-full sm:w-auto"
            >
              {converting ? 'Converting...' : 'Convert to Rollover'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
